// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Focus sessions are local-first: every session row lives in local SQLite via Tauri IPC
// (durable, offline, on-disk). Data model mirrors the server brain's contract exactly:
//   focus_session { duration_min, started_at, completed_at?, reflection? }
// Nothing here leaves the Mac — only the scoped week of sessions is sent to the AI brain.

import { invoke } from "./tauri-web";

export interface FocusSession {
  id: string;
  duration_min: number;
  started_at: string;          // ISO 8601
  completed_at: string | null; // ISO 8601 when the session ran to completion; null if stopped early
  reflection: string | null;
  created_at: string;
  goal_id: string | null;
  /** ON-DEVICE photo of the session (downscaled JPEG data-URL). Never sent to the model. */
  photo_ref: string;
}

export interface NewFocusSession {
  duration_min: number;
  started_at: string;
  completed_at?: string | null;
  reflection?: string | null;
  goal_id?: string | null;
}

export interface Settings {
  default_duration_min: number;
}

// ── Derived signals (computed in Rust on-device; only this aggregate leaves the Mac) ──
// Mirrors db.rs `WeeklySignals`. NO raw reflection text, NO per-session rows — counts,
// rates, histograms, totals, a trend slope, streaks, std-devs, and pre-ranked headline facts.
export type FactTone = "improving" | "rising" | "steady" | "notable" | "neutral";
// A pre-ranked, ready-to-narrate observational sentence (figure + unit baked in). The AI
// leans on these directly; the UI shows them under the narration.
export interface HeadlineFact {
  rank: number;        // 0 = most notable
  kind: string;        // stable category id (volume / streak / peak_time / …)
  tone: FactTone;      // reading hint only — never a verdict
  text: string;
}
export interface WeeklySignals {
  schema: string;
  window: { days: number; start: string; end: string; generated_at: string };
  sessions_total: number;
  completed_total: number;
  completion_rate: number; // 0..1
  total_focus_minutes: number;
  avg_duration_min: number;
  distinct_active_days: number;
  durations_offered: number[];
  hour_clustering: {
    morning: number;
    afternoon: number;
    evening: number;
    night: number;
    histogram_24h: number[];
    peak_band: string;
    peak_hour: number | null;
  };
  reflections: {
    sessions_total: number;
    with_reflection: number;
    reflection_rate: number;
    avg_length_chars: number;
    max_length_chars: number;
  };
  week_over_week: {
    this_week: WeekTotals;
    prior_week: WeekTotals;
    sessions_delta: number;
    minutes_delta: number;
    minutes_pct_change: number | null;
  };
  duration_trend: {
    slope_min_per_session: number;
    first_duration_min: number | null;
    last_duration_min: number | null;
    n: number;
  };
  active_day_streaks: {
    current_streak: number;
    longest_streak: number;
    busiest_day: string | null;
    busiest_day_sessions: number;
    busiest_day_minutes: number;
  };
  duration_volatility: {
    mean_min: number;
    std_dev_min: number;
    min_min: number;
    max_min: number;
    range_min: number;
    label: string;
  };
  completion_by_band: {
    bands: { band: string; sessions: number; completed: number; completion_rate: number }[];
    strongest_band: string | null;
    strongest_rate: number | null;
    weakest_band: string | null;
    weakest_rate: number | null;
  };
  weekday_pattern: {
    horizon_days: number;
    days: { weekday: string; sessions: number; total_minutes: number; completion_rate: number | null }[];
    busiest_weekday: string | null;
    busiest_weekday_sessions: number;
  };
  headline_facts: HeadlineFact[];
}
export interface WeekTotals {
  sessions: number;
  completed: number;
  total_minutes: number;
  avg_duration_min: number;
}

// ── Focus Plan — the AI-deepening feature (mirrors Workout Tracker's Program design) ──
export type Band = "morning" | "afternoon" | "evening" | "night";
export interface FocusPlan {
  id: string;
  name: string;
  status: "active" | "abandoned";
  created_at: string;
}
export interface PlannedSession {
  id: string;
  plan_id: string;
  weekday: number; // 0=Mon..6=Sun
  band: Band;
  duration_min: number;
  label: string;
  reason: string;
  completed_session_id: string | null;
}
export interface NewPlannedSession {
  weekday: number;
  band: Band;
  duration_min: number;
  label: string;
  reason: string;
}
export interface NewFocusPlan {
  name: string;
  sessions: NewPlannedSession[];
}

// ── Goals — "what you're working toward" (Learn Spanish, Finish my novel…), not just minutes.
export interface Goal {
  id: string;
  title: string;
  created_at: string;
  archived_at: string | null;
}
export interface NewGoal {
  title: string;
}
export interface GoalStats {
  goal_id: string;
  sessions_total: number;
  total_minutes: number;
  last_session_at: string | null;
  this_week_minutes: number;
  prior_week_minutes: number;
}

export const store = {
  listSessions: (limit?: number) =>
    invoke<FocusSession[]>("list_sessions", { limit: limit ?? 50000 }),
  // The scoped slice (raw rows) — kept for any local use; NOT what the AI brain receives.
  recentSessions: (days: number) =>
    invoke<FocusSession[]>("recent_sessions", { days }),
  // The derived-signal aggregate the AI brain receives — computed in Rust, on-device.
  weeklySignals: (days: number) =>
    invoke<WeeklySignals>("weekly_signals", { days }),
  createSession: (input: NewFocusSession) =>
    invoke<FocusSession>("create_session", { input }),
  deleteSession: (id: string) => invoke<void>("delete_session", { id }),
  /** File an on-device photo onto a session (chat 📷 turn). Pass "" to clear it. */
  setSessionPhoto: (id: string, photo_ref: string) =>
    invoke<void>("set_session_photo", { id, photo_ref }),

  getSettings: () => invoke<Settings>("get_settings"),
  setDefaultDuration: (minutes: number) =>
    invoke<void>("set_default_duration", { minutes }),

  activePlan: () => invoke<FocusPlan | null>("active_plan"),
  listPlannedSessions: (plan_id: string) =>
    invoke<PlannedSession[]>("list_planned_sessions", { plan_id }),
  nextPlannedSession: () => invoke<PlannedSession | null>("next_planned_session"),
  createFocusPlan: (input: NewFocusPlan) =>
    invoke<FocusPlan>("create_focus_plan", { input }),
  abandonActivePlan: () => invoke<void>("abandon_active_plan"),
  completePlannedSession: (planned_id: string, session_id: string) =>
    invoke<void>("complete_planned_session", { planned_id, session_id }),

  createGoal: (input: NewGoal) => invoke<Goal>("create_goal", { input }),
  listGoals: (include_archived = false) => invoke<Goal[]>("list_goals", { include_archived }),
  archiveGoal: (goal_id: string) => invoke<void>("archive_goal", { goal_id }),
  deleteGoal: (goal_id: string) => invoke<void>("delete_goal", { goal_id }),
  goalStats: (goal_id: string) => invoke<GoalStats>("goal_stats", { goal_id }),
};
